import type { components } from '@/api/client';
import type { NoteType, NoteViewModel } from '@/features/patient-record/types';
import { narrowEnum, nullableString } from '@/api/mappers/_shared';

type NoteDto = components['schemas']['Note'];

/**
 * Live Note response carries newer fields (`author_account_id` /
 * `authorAccountId`, `modified_on` / `modifiedOn`) that the generated schema
 * snapshot doesn't yet model. We read both casings defensively so the FE
 * keeps working through a contract regeneration without a code change.
 */
type LiveNoteDto = NoteDto & {
  readonly user_id?: string | null;
  readonly author_account_id?: string | null;
  readonly authorAccountId?: string | null;
  readonly modified_on?: string | null;
  readonly modifiedOn?: string | null;
};

const KNOWN_NOTE_TYPE: ReadonlySet<NoteType> = new Set<NoteType>([
  'NOTE',
  'EMAIL',
  'CALL',
]);

function pickFirstString(...values: readonly unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return null;
}

export function mapNote(dto: NoteDto): NoteViewModel {
  const live = dto as LiveNoteDto;
  return {
    id: dto.id,
    noteType: narrowEnum<NoteType>(dto.note_type, KNOWN_NOTE_TYPE, 'NOTE'),
    subject: nullableString(dto.subject),
    body: dto.body,
    authorName: nullableString(dto.author_name),
    authorAccountId: pickFirstString(
      live.author_account_id,
      live.authorAccountId,
      live.user_id,
    ),
    createdAt: nullableString(dto.created_at),
    modifiedOn: pickFirstString(live.modified_on, live.modifiedOn),
  };
}

export function mapNotes(dtos: readonly NoteDto[]): NoteViewModel[] {
  return dtos.map(mapNote);
}
